import {PayType} from 'com/gongxigames/math/core/game/enums/PayType';
import {Line} from 'com/gongxigames/math/core/game/Line';
import {Graphics, Sprite, DisplayObject} from 'pixi.js';
import {CoreDI, IAnimation, inject, ObjectBuilder, ResolutionManager, Timer, UpdateManager, Map} from '@gongxigames/framework/core';
import {LAYERS} from '@gongxigames/framework/slot/configs/Layers';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {GameConfig, ILogic, IReelsScaleService} from '@gongxigames/framework/slot/types';
import {PayAction, PayActionMap, WinPresentationViewEvents, WinTypeConfig} from '@gongxigames/framework/slot/modules/winpresentation';
import {ReelConstants, ReelsOrdering, SymbolPosition, SymbolsOrdering} from '@gongxigames/framework/slot/utils';
import {WinPresentationUtils} from '@gongxigames/framework/slot/services/WinPresentationUtils';
import {View} from '@gongxigames/framework/slot/fragments/module/view';
import {Action, Pay, SpinData, Position} from '@gongxigames/framework/slotV2/types/LogicData';
/**
 * [minimun flow multiplier] : list of the possible sounds to play for this value
 */

export class WinPresentationView extends View {
    public static readonly NAME: symbol = Symbol('WinPresentationView');
    public static layer = LAYERS.REELS_WIN;

    public static readonly ADDITIONAL_HEIGHT: number = 331;

    @inject(DI.Logic)
    protected _logic!: ILogic;

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    @inject(DI.WinPresentationUtils)
    protected _winPresentationUtils!: WinPresentationUtils;

    @inject(CoreDI.ResolutionManager)
    protected _resolutionManager!: ResolutionManager;

    @inject(DI.ReelsScaleService)
    protected _reelsScaleService!: IReelsScaleService;

    // tracks whether all flow highlights for a round have been displayed
    protected _allWinHighlightsShown = false;

    protected _winTypeConfig!: WinTypeConfig;

    // line or way, or scatterPay
    protected _pays: Pay[] = [];

    protected _payActions: PayActionMap = {};
    protected _currentPay = 0;
    protected _updater: UpdateManager = new UpdateManager(true);

    protected _nextSoundToPlayIndex: Map<number> = {};
    protected _currentPlayingSound = '';
    protected _allPayAnimationsFinishedEventEmitted = false;

    protected _isInFreeSpins = false;

    protected _currentRestoreSpin?: SpinData;

    // only for lines game
    private _lines!: Map<Sprite>;

    public init(): void {
        super.init();

        this._isInFreeSpins = false;

        this.initWinSoundsConfig();

        this.updateManager.add(this._updater);
    }

    public onResourcesLoaded(): void {
        if (this.isNeedToCacheAnimations()) {
            this.cacheAnimations();
        }
    }

    public play(): void {
        this.setPaysData(this._logic.spinData.pays);

        // add mask in order to prevent showing animations under the bottom bar
        if (this.isPlatformDesktop()) {
            this.addMask();
        }

        this.playInitialAnimation();

        if (this.isFreeSpinsWon() || this._logic.isRetriggerWon()) {
            this.soundPlayer.play('freespintrigger');
        } else {
            this.playWinSound();
        }
    }

    public setPaysForRestore(spin: SpinData): void {
        this._currentRestoreSpin = spin;
        this.setPaysData(spin.pays);
    }

    public setPaysData(pays: Pay[]): void {
        this._currentPay = 0;
        this._allWinHighlightsShown = false;
        this._allPayAnimationsFinishedEventEmitted = false;
        this._winTypeConfig = this._winPresentationUtils.getWinTypeConfig()!;
        this._pays = this.getPays(pays);
        this._payActions = this._winPresentationUtils.parseWinActions(this._pays);
    }

    public playPayAnimation(isRestore = false): void {
        this.clearPreviousPayAnimation();

        if (!this._pays.length) {
            return;
        }

        let needToEmitAllPayAnimationsFinished = false;

        // if we've reached the last pay - cycle back to the first
        if (this._currentPay === this._pays.length) {
            this._allWinHighlightsShown = true;
            this._currentPay = 0;

            needToEmitAllPayAnimationsFinished = true;
        }

        const payAction = this._payActions[this._currentPay];
        const pay = this._pays[this._currentPay];

        if (isRestore) {
            this.configurePayRestore(pay, payAction);
        } else {
            this.configurePay(pay, payAction);
        }

        this.emit(WinPresentationViewEvents.PAY_ANIMATION_STARTED, pay);

        // increment index for next time
        this._currentPay++;

        // this part must to be at this exact place
        if (needToEmitAllPayAnimationsFinished) {
            this.emitAllPayAnimationsFinished();
        }
    }

    public playPaysAnimationOneCycle(): void {
        this.clearPreviousPayAnimation();

        if (!this._pays.length) {
            return;
        }

        // if we've reached the last pay - cycle back to the first
        if (this._currentPay === this._pays.length) {
            this._allWinHighlightsShown = true;
            this._currentPay = 0;

            if (this.emitAllPayAnimationsFinished()) {
                return;
            }
        }

        const payAction = this._payActions[this._currentPay];
        const pay = this._pays[this._currentPay];

        this.configurePayOneCycle(pay, payAction);
        this.emit(WinPresentationViewEvents.PAY_ANIMATION_STARTED, pay);

        // increment index for next time
        this._currentPay++;
    }

    protected isNotSkippedPays(): boolean {
        return this._currentPay === 0 && ![PayType.LINE, PayType.WAY].includes(this._pays[this._currentPay]?.type);
    }

    public skipAllPayWinAnimations(): void {
        if (this._pays.length) {
            if (this.isNotSkippedPays()) {
                return;
            }

            this._allWinHighlightsShown = true;
            this._currentPay = 0;

            this.emitAllPayAnimationsFinished(true);
        }
    }

    protected emitAllPayAnimationsFinished(isSkip = false): boolean {
        if (!this._allPayAnimationsFinishedEventEmitted || isSkip) {
            this._allPayAnimationsFinishedEventEmitted = true;

            this.emit(WinPresentationViewEvents.ALL_PAY_ANIMATIONS_FINISHED);

            return true;
        }

        return false;
    }

    protected cacheAnimations(): void {
        if (this._gameConfig.winConfig.normal && this._gameConfig.winConfig.normal.regular[0]) {
            for (const animation of this._gameConfig.winConfig.normal.regular[0].animation) {
                if (animation.spine && animation.cached) {
                    this.spineUtils.createCachedAnimation(animation.spine, animation.animation);
                }
            }
        }
    }

    // TODO: Remove
    protected initLines(): void {
        const additionalSpace = 50;
        const lines = this._logic.getLines()!;

        this._lines = {};

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const reels = ReelConstants.REELS.length;
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d')!;
            const topLeft = this._winPresentationUtils.getSymbolPosition(0, 0);
            const bottomRight = this._winPresentationUtils.getSymbolPosition(reels - 1,
                ReelConstants.REELS[reels - 1].symsPositions.length - 1);
            const resolution = this._screenManager.defaultViewportResolution;

            canvas.width = resolution * (bottomRight.x - topLeft.x + additionalSpace * 2);
            canvas.height = resolution * (bottomRight.y - topLeft.y + additionalSpace * 2);

            this.applyLineStyle(ctx, resolution);

            const points: { x: number; y: number; }[] = [];

            for (let reel = 0; reel < reels; reel++) {
                const pos = this.getLineSymbolPosition(line, reel);

                points.push({
                    x: pos.x - topLeft.x + additionalSpace,
                    y: pos.y - topLeft.y + additionalSpace
                });
            }

            for (let i = 0; i < 3; i++) {
                ctx.beginPath();

                for (let j = 1; j < points.length; j++) {
                    const point = points[j];

                    ctx.moveTo(resolution * points[j - 1].x, resolution * points[j - 1].y);
                    ctx.lineTo(resolution * point.x, resolution * point.y);
                }

                ctx.stroke();
            }

            const sprite = Sprite.from(canvas);

            sprite.x = topLeft.x - additionalSpace + ReelConstants.SYMBOL.width / 2;
            sprite.y = topLeft.y - additionalSpace + ReelConstants.SYMBOL.height / 2;
            sprite.scale.set(1 / resolution);

            this._lines[i] = sprite;
        }
    }

    protected applyLineStyle(ctx: CanvasRenderingContext2D, resolution: number): void {
        ctx.lineWidth = 20 * resolution;
        ctx.strokeStyle = '#fff600';
        ctx.shadowColor = '#ff6c00';
        ctx.shadowBlur = 30 * resolution;
        ctx.lineCap = 'round';
    }

    protected getLineSymbolPosition(line: Line, reel: number): SymbolPosition {
        return this._winPresentationUtils.getSymbolPosition(reel, line.getRow(reel));
    }

    protected isFreeSpinsWon(): boolean {
        return this._logic.nextAction === Action.FREESPIN && this._logic.previousAction === Action.SPIN;
    }

    protected initWinSoundsConfig(): void {
        const winTypes = this._winPresentationUtils.getWinConfig().winTypes;

        for (const key of winTypes) {
            this._nextSoundToPlayIndex[key.name] = 0;
        }
    }

    /**
     * according to the requirements from game designer
     * we need to play one of the few possible flow sounds for different flow values
     * and NOT to play the same soundManager two times in a row
     */
    public playWinSound(win?: number): void {
        const winType = this._winPresentationUtils.getWinTypeConfigForSpinWin(win);

        if (winType) {
            const sounds = winType.sounds;

            if (sounds) {
                if (this._nextSoundToPlayIndex[winType.name] === undefined) {
                    this._nextSoundToPlayIndex[winType.name] = 0;
                }

                if (sounds[this._nextSoundToPlayIndex[winType.name]] === 'bigwin' && !win) {
                    return;
                }

                this._currentPlayingSound = sounds[this._nextSoundToPlayIndex[winType.name]];
                this.soundPlayer.play(this._currentPlayingSound);
                this._nextSoundToPlayIndex[winType.name]++;

                if (this._nextSoundToPlayIndex[winType.name] >= sounds.length) {
                    this._nextSoundToPlayIndex[winType.name] = 0;
                }
            }
        }
    }

    protected stopWinSound(): void {
        if (this._currentPlayingSound !== '') {
            this.soundPlayer.stop(this._currentPlayingSound);
            this._currentPlayingSound = '';
        }
    }

    protected getPays(payList: Pay[]): Pay[] {
        const pays: Pay[] = [];

        payList.sort((a, b) => {
            if (this._logic.isLinesGame()) {
                return a.lineId - b.lineId;
            } else {
                if (b.symbolId === this._gameConfig.reelsConfig.scatterIndex) {
                    return 1;
                }

                return b.win - a.win;
            }
        });

        for (const pay of payList) {
            if (pay.type === PayType.SCATTER || pay.type === PayType.LINE || pay.type === PayType.WAY) {
                if (pay.type === PayType.SCATTER) {
                    pays.unshift(pay);
                } else {
                    pays.push(pay);
                }
            }
        }

        return pays;
    }

    public stop(): void {
        this._currentRestoreSpin = undefined;
        this.removeChildrenFromContainer();
        this._updater.removeAll();
        this.stopWinSound();
    }

    public resetPaysData(): void {
        this._pays.length = 0;
    }

    protected isNeedToCacheAnimations(): boolean {
        return this._device.isSuperLowQuality && !this._device.isAppleDevice;
    }

    protected playInitialAnimation(): void {
        const initialConfig = this._winPresentationUtils.getWinConfig().initial;

        this.configureInitialAnimationSymbols();
        this._updater.add(new Timer(this.onInitialAnimationFinished.bind(this), initialConfig.time, true));
    }

    protected clearPreviousPayAnimation(): void {
        this._updater.removeAll();
        this.removeChildrenFromContainer();
    }

    protected configureInitialAnimationSymbols(): void {
        this.configureSymbols(this._pays, this._winPresentationUtils.getWinConfig().initial);
        this.emit(WinPresentationViewEvents.INITIAL_ANIMATION_STARTED, this._pays);
    }

    protected onInitialAnimationFinished(): void {
        if (this._isInFreeSpins) {
            this.emit(WinPresentationViewEvents.INITIAL_ANIMATION_FREESPINS_FINISHED);
        } else {
            this.emit(WinPresentationViewEvents.INITIAL_ANIMATION_FINISHED);
        }
    }

    protected configurePayRestore(pay: Pay, payAction: PayAction): void {
        this.showPayLine(pay.lineId);
        this.configureSymbols([pay], payAction, this._currentRestoreSpin!.symbolIds);
        this._updater.add(new Timer(this.playPayAnimation.bind(this, true), payAction.time, true));
    }

    protected configurePay(pay: Pay, payAction: PayAction): void {
        this.showPayLine(pay.lineId);
        this.configureSymbols([pay], payAction, this._logic.spinData.symbolIds);
        this._updater.add(new Timer(this.playPayAnimation.bind(this), payAction.time, true));
    }

    protected getWinSymbols(pays: Pay[]): Position[] {
        const winSymbolIndexes: Position[] = [];

        const isEqualPosition = (position1: Position, position2: Position): boolean => {
            return position1.reel === position2.reel && position1.row === position2.row;
        };

        const hasPositionInWinSymbolIndexes = (position: Position): boolean => {
            return !!winSymbolIndexes.filter(value => isEqualPosition(value, position)).length;
        };

        for (const pay of pays) {
            for (const pos of pay.positions) {
                if (!hasPositionInWinSymbolIndexes(pos)) {
                    winSymbolIndexes.push(pos);
                }
            }
        }

        return this.reorderWinSymbols(winSymbolIndexes);
    }

    /* Firstly symbols sorted by defaultSymbolsOrder.
    If symbols equal and they placed on the same row - they will be sorted by row order.
    If symbols equal and they on the different reels - they will be sorted by reel order.
     */

    protected reorderWinSymbols(positions: Position[]): Position[] {
        const winSymbols = [...positions];
        const getOrderIndex = (symbolId: number): number => {
            return this._gameConfig.reelsConfig.defaultSymbolsOrder.findIndex((orderID) => {
                return this.isSymbolsEqual(orderID, symbolId);
            });
        };

        const rowOrder = (a: Position, b: Position): number => {
            if (ReelConstants.SYMBOLS_ORDERING === SymbolsOrdering.TOP_TO_BOTTOM) {
                return a.row - b.row;
            } else if (ReelConstants.SYMBOLS_ORDERING === SymbolsOrdering.BOTTOM_TO_TOP) {
                return b.row - a.row;
            }

            return 0;
        };

        const reelOrder = (a: Position, b: Position): number => {
            if (ReelConstants.REELS_ORDERING === ReelsOrdering.LEFT_TO_RIGHT) {
                return a.reel - b.reel;
            }

            return b.reel - a.reel;
        };

        winSymbols.sort((a, b) => {
            const symbolOrder = getOrderIndex(a.symbolId) - getOrderIndex(b.symbolId);
            const rowOrdering = rowOrder(a, b);

            if (symbolOrder) {
                return symbolOrder;
            } else if (rowOrdering) {
                return rowOrdering;
            }

            return reelOrder(a, b);
        });

        return winSymbols;
    }

    protected isSymbolsEqual(orderSymbol: number | number[], rowSymbol: number): boolean {
        if (orderSymbol instanceof Array) {
            return orderSymbol.indexOf(rowSymbol) !== -1;
        }

        return orderSymbol === rowSymbol;
    }

    protected showPayLine(lineId: number): void {
        if (this._logic.isLinesGame()) {
            this.showLine(lineId);
        }
    }

    protected showLine(_index: number): void {}

    protected configurePayOneCycle(pay: Pay, payAction: PayAction): void {
        this.showPayLine(pay.lineId);
        this.configureSymbols([pay], payAction, this._logic.spinData.symbolIds);
        this._updater.add(new Timer(this.playPaysAnimationOneCycle.bind(this), payAction.time, true));
    }

    protected configureSymbols(pays: Pay[], payAction: PayAction, symbolIds?: number[][]): void {
        this.getWinSymbols(pays).forEach((pos) => {
            this.configureSymbol(payAction, symbolIds ? symbolIds[pos.reel][pos.row] : pos.symbolId, pos);
        });
    }

    protected configureSymbol(payAction: PayAction, symbol: number, pos: Position): IAnimation {
        const animation = this._winPresentationUtils.createSymbolAnimation(payAction, symbol, pos);

        this._updater.add(animation);
        this.container.addChild(animation.container);
        // this.addFrameAnimation(pos);

        animation.start();

        return animation;
    }

    protected addFrameAnimation(pos: Position): void {
        const position = this._winPresentationUtils.getSymbolPosition(pos.reel, pos.row);
        const animation = this.objectBuilder.buildAnimation(ObjectBuilder.resources.game_sprite.winFrame);

        this._updater.add(animation);
        this.container.addChild(animation.container);
        animation.container.x += position.x;
        animation.container.y += position.y;
        animation.start();
    }

    protected addMask(): void {
        let mask: Graphics | undefined = this.container.mask as Graphics;
        const hasMask = mask && mask.parent === this.container;

        if (hasMask) {
            return;
        }

        mask = this.createMask();

        if (mask) {
            this.container.addChild(mask);
            this.container.mask = mask;
        }
    }

    protected createMask(): Graphics | undefined {
        const mask = new Graphics();

        mask.beginFill(0xffffff);
        mask.drawRect(0, 0, 1, 1);
        mask.endFill();
        mask.width = this._gameConfig.viewport.desktop.width;
        mask.height = ReelConstants.SYMBOL_SLOT.height * ReelConstants.ROW_AMOUNT + WinPresentationView.ADDITIONAL_HEIGHT;

        return mask;
    }

    public onOrientationChanged(): void {
        super.onOrientationChanged();

        this._reelsScaleService.scaleByCurrentScreenOrientation(this.container);
    }

    public get isInFreeSpins(): boolean {
        return this._isInFreeSpins;
    }

    public set isInFreeSpins(value: boolean) {
        this._isInFreeSpins = value;
    }

    private removeChildrenFromContainer(): void {
        this.container.removeChildren();

        if (this.container.mask && this.container.mask instanceof DisplayObject) {
            this.container.addChild(this.container.mask);
        }
    }
}
